/**
 * Display helpers for user names in dense tables (Access Control).
 *
 * Long names used to wrap inside the sticky User column and become
 * unreadable. The table shows a 15-character name; the full name and
 * email stay on the native hover tooltip.
 */

export const USER_NAME_MAX_CHARS = 15;

export function displayUserName(user) {
  if (user == null) return '';
  if (typeof user === 'string') return user.trim();
  if (typeof user !== 'object') return '';
  const name = String(user.name || user.full_name || '').trim();
  const email = String(user.email || '').trim();
  return name || email;
}

export function truncateUserName(name, max = USER_NAME_MAX_CHARS) {
  const limit = Number.isFinite(max) && max > 0 ? max : USER_NAME_MAX_CHARS;
  const s = String(name || '').trim();
  if (s.length <= limit) return s;
  return `${s.slice(0, limit).trimEnd()}…`;
}

/** Native ``title`` / long-press copy: full name and email. */
export function userHoverLabel(name, email) {
  const n = String(name || '').trim();
  const e = String(email || '').trim();
  if (n && e && n.toLowerCase() !== e.toLowerCase()) {
    return `${n} — ${e}`;
  }
  return n || e;
}

export function userMatchesQuery(user, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  if (!user || typeof user !== 'object') return false;
  const hay = [
    user.name,
    user.full_name,
    user.email,
    user.username,
    user.permission_group_name,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

/** Max name-search hits shown when adding members to a permission group. */
export const GROUP_MEMBER_SUGGESTION_LIMIT = 8;

/**
 * Name-search suggestions for the User Groups member picker.
 *
 * Returns nothing until the admin types something — this is a one-by-one
 * add flow, not a full directory list. Already-selected people and admins
 * are omitted. People already in a *different* group stay in the list so
 * the admin can see the match, but they are flagged ``inOtherGroup``.
 */
export function groupMemberSuggestions(
  users,
  {
    query,
    selectedIds = [],
    selectedGroupId = '',
    limit = GROUP_MEMBER_SUGGESTION_LIMIT,
  } = {},
) {
  const q = String(query || '').trim();
  if (!q) return [];

  const selected = new Set((selectedIds || []).map((id) => String(id)));
  const addable = [];
  const blocked = [];

  for (const user of users || []) {
    if (!user || typeof user !== 'object') continue;
    if (user.is_admin) continue;
    if (selected.has(String(user.id))) continue;
    if (!userMatchesQuery(user, q)) continue;
    const inOtherGroup = !!(
      user.permission_group_id
      && String(user.permission_group_id) !== String(selectedGroupId || '')
    );
    (inOtherGroup ? blocked : addable).push({ user, inOtherGroup });
  }

  return [...addable, ...blocked].slice(0, limit);
}
